import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface AppNavBackTarget {
  /** Router path segments */
  commands: (string | number)[];
  queryParams?: Record<string, string | number | boolean | null | undefined>;
  label: string;
}

@Injectable({ providedIn: 'root' })
export class AppNavBackService {
  constructor(private readonly router: Router) {}

  /** Resolve a sensible parent route and label for the current (or given) URL. */
  resolveBackTarget(rawUrl?: string): AppNavBackTarget | null {
    const path = this.normalizePath(rawUrl ?? this.router.url);
    const segments = path.split('/').filter(Boolean);

    const plotMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/places\/([^/]+)\/plot-(?:edit|view)\/([^/]+)/,
    );
    if (plotMatch) {
      const [, teamCode, appId, placesId] = plotMatch;
      return {
        commands: ['/team', teamCode, 'application', appId, 'places', placesId],
        label: '場域',
      };
    }

    const plotListMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/places\/([^/]+)\/plot-list/,
    );
    if (plotListMatch) {
      const [, teamCode, appId, placesId] = plotListMatch;
      return {
        commands: ['/team', teamCode, 'application', appId, 'places', placesId],
        label: '場域',
      };
    }

    // 用電變更案件詳情 → 用電變更列表
    const planChangeDetailMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/places\/([^/]+)\/project\/([^/]+)\/electricity-plan-change\/electricity-detail\/[^/]+$/,
    );
    if (planChangeDetailMatch) {
      const [, teamCode, appId, placesId, projectId] = planChangeDetailMatch;
      return {
        commands: [
          '/team', teamCode, 'application', appId, 'places', placesId,
          'project', projectId, 'electricity-plan-change',
        ],
        label: '用電變更',
      };
    }

    // 用電變更列表 → 場域控制台
    const planChangeListMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/places\/([^/]+)\/project\/([^/]+)\/electricity-plan-change$/,
    );
    if (planChangeListMatch) {
      const [, teamCode, appId, placesId] = planChangeListMatch;
      return {
        commands: ['/team', teamCode, 'application', appId, 'places', placesId],
        label: '場域',
      };
    }

    // 電價／電力管理（電號層）→ 場域
    const priceOrPowerMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/places\/([^/]+)\/(?:electricity-price-management|electricity-power-management)\/[^/]+$/,
    );
    if (priceOrPowerMatch) {
      const [, teamCode, appId, placesId] = priceOrPowerMatch;
      return {
        commands: ['/team', teamCode, 'application', appId, 'places', placesId],
        label: '場域',
      };
    }

    const placeSubMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/places\/([^/]+)\/(.+)/,
    );
    if (placeSubMatch) {
      const [, teamCode, appId, placesId, rest] = placeSubMatch;
      const sub = rest.split('/')[0];
      if (sub === 'place' || sub === 'project' || sub === 'management') {
        return {
          commands: ['/team', teamCode, 'application', appId, 'places', placesId],
          label: '場域',
        };
      }
      if (sub === 'plot-list' || sub === 'chatroom' || sub === 'setting' || sub === 'device-monitor' || sub === 'dashboard') {
        return {
          commands: ['/team', teamCode, 'application', appId, 'places', placesId],
          label: '場域',
        };
      }
    }

    const placesIdMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/places\/([^/]+)$/,
    );
    if (placesIdMatch) {
      const [, teamCode, appId] = placesIdMatch;
      return {
        commands: ['/team', teamCode, 'application', appId, 'places'],
        label: '場域列表',
      };
    }

    const appPlacesMatch = path.match(/^\/team\/([^/]+)\/application\/(\d+)\/places$/);
    if (appPlacesMatch) {
      const [, teamCode] = appPlacesMatch;
      return this.imaginusHomeTarget(teamCode);
    }

    const appMgmtMatch = path.match(
      /^\/team\/([^/]+)\/application\/(\d+)\/management(?:\/(.+))?$/,
    );
    if (appMgmtMatch) {
      const [, teamCode, appId, sub] = appMgmtMatch;
      if (sub) {
        return {
          commands: ['/team', teamCode, 'application', appId, 'management'],
          label: '管理',
        };
      }
      const query = (rawUrl ?? this.router.url).split('?')[1] || '';
      const section = new URLSearchParams(query).get('section');
      if (section === 'tools') {
        return {
          commands: ['/team', teamCode, 'application', appId, 'management'],
          queryParams: { section: 'overview' },
          label: '服務管理',
        };
      }
      if (section && section !== 'overview') {
        return {
          commands: ['/team', teamCode, 'application', appId, 'management'],
          queryParams: { section: 'overview' },
          label: '服務管理',
        };
      }
      return this.imaginusHomeTarget(teamCode);
    }

    const teamHomeMatch = path.match(/^\/team\/([^/]+)\/(home|mailbox|task-manager|user-profile)$/);
    if (teamHomeMatch) {
      const [, teamCode] = teamHomeMatch;
      return {
        commands: ['/home'],
        queryParams: { team: teamCode },
        label: 'IMA One',
      };
    }

    const teamAppMatch = path.match(/^\/team\/([^/]+)\/application\/(\d+)\/(.+)/);
    if (teamAppMatch) {
      const [, teamCode, appId] = teamAppMatch;
      return {
        commands: ['/team', teamCode, 'home'],
        label: '團隊首頁',
      };
    }

    const teamRootMatch = path.match(/^\/team\/([^/]+)$/);
    if (teamRootMatch) {
      return {
        commands: ['/home'],
        label: 'IMA One',
      };
    }

    if (path === '/imaginus' || path === '/home') {
      return null;
    }

    if (path.startsWith('/enterprise/')) {
      return {
        commands: ['/select-identity'],
        label: '選擇身份',
      };
    }

    if (path === '/settings' || path === '/mailbox' || path === '/task-manager') {
      return {
        commands: ['/home'],
        label: 'IMA One',
      };
    }

    if (segments.length >= 2) {
      const parent = '/' + segments.slice(0, -1).join('/');
      const parentTarget = this.resolveBackTarget(parent);
      if (parentTarget) {
        return parentTarget;
      }
    }

    return null;
  }

  navigateBack(rawUrl?: string): boolean {
    const target = this.resolveBackTarget(rawUrl);
    if (target) {
      void this.router.navigate(target.commands, {
        queryParams: target.queryParams,
      });
      return true;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
      return true;
    }
    void this.router.navigate(['/home']);
    return true;
  }

  /** 是否為 Imaginus 首頁（/home）返回目標 */
  isImaginusHomeCommands(commands: (string | number)[]): boolean {
    const parts = commands.map((c) => String(c).replace(/^\/+/, '')).filter(Boolean);
    return parts.length === 1 && parts[0] === 'home';
  }

  isImaginusHomeTarget(target: AppNavBackTarget | null | undefined): boolean {
    if (!target?.commands?.length) {
      return false;
    }
    return this.isImaginusHomeCommands(target.commands);
  }

  private normalizePath(raw: string): string {
    let path = (raw || '').split('?')[0].split('#')[0];
    if (path.includes('#')) {
      path = path.split('#').pop() || path;
    }
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return path.replace(/\/+$/, '') || '/';
  }

  private imaginusHomeTarget(teamCode?: string): AppNavBackTarget {
    return {
      commands: ['/home'],
      queryParams: teamCode ? { team: teamCode } : undefined,
      label: 'IMA One',
    };
  }
}
